import * as React from 'react'
import { FileImage, FileText } from 'lucide-react'
import { cn } from '../../lib/utils'
import { parseDocumentPreview, type DocumentPreviewDocument, type DocumentPreviewImage, type DocumentPreviewParagraph, type DocumentPreviewTable, type OfficePreviewImageAsset } from '../../lib/office-preview'
import { FullscreenOverlayBase } from './FullscreenOverlayBase'
import { FullscreenOverlayBaseHeader } from './FullscreenOverlayBaseHeader'
import { PreviewOverlay } from './PreviewOverlay'
import { useTranslation } from '@okclawpro/i18n/react'

export interface OfficeDocumentPreviewOverlayProps {
  content: string
  isOpen: boolean
  onClose: () => void
  filePath: string
  theme?: 'light' | 'dark'
  error?: string
  embedded?: boolean
}

function DocumentTable({ block }: { block: DocumentPreviewTable }) {
  const { t } = useTranslation()
  return (
    <div className="my-5 overflow-x-auto rounded-md border border-foreground/[0.1]">
      <table className="w-full min-w-[520px] border-separate border-spacing-0 text-[12px] leading-5">
        <tbody>
          {block.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex === 0 ? 'bg-foreground/[0.045] font-semibold' : 'hover:bg-foreground/[0.025]'}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="border-b border-r border-foreground/[0.075] px-3 py-2 align-top last:border-r-0">
                  {cell || '\u00a0'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {block.truncated && <div className="border-t border-foreground/[0.08] px-3 py-1.5 text-[10px] text-muted-foreground">{t('overlay.lightPreviewRange')}</div>}
    </div>
  )
}

function DocumentParagraph({ block }: { block: DocumentPreviewParagraph }) {
  if (block.role === 'title') return <h1 className="mb-5 text-[25px] font-semibold leading-tight tracking-[-0.025em] text-foreground">{block.text}</h1>
  if (block.role === 'subtitle') return <p className="mb-5 text-[13px] leading-6 text-muted-foreground">{block.text}</p>
  if (block.role === 'heading') {
    const Heading = block.level <= 1 ? 'h2' : 'h3'
    return <Heading className={cn('mt-7 mb-2.5 font-semibold tracking-[-0.015em] text-foreground', block.level <= 1 ? 'text-[18px]' : 'text-[15px]')} style={{ marginLeft: block.level > 1 ? (block.level - 1) * 8 : 0 }}>{block.text}</Heading>
  }
  if (block.role === 'list') {
    return <p className="my-1.5 flex gap-2 text-[13px] leading-6 text-foreground/80" style={{ paddingLeft: block.level * 18 }}><span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" aria-hidden="true" /> <span>{block.text}</span></p>
  }
  return <p className="my-3 text-[13px] leading-[1.85] text-foreground/80">{block.text}</p>
}

function DocumentImage({ block, asset, t }: { block: DocumentPreviewImage; asset?: OfficePreviewImageAsset; t: (key: any) => string }) {
  const sourceRatio = block.width && block.height ? block.width / block.height : asset?.width && asset.height ? asset.width / asset.height : undefined
  if (!asset?.dataUrl) return <div className="my-5 flex min-h-28 items-center justify-center rounded-md border border-dashed border-foreground/[0.14] bg-foreground/[0.02] text-muted-foreground"><FileImage className="h-5 w-5 opacity-45" aria-hidden="true" /><span className="ml-2 text-[11px]">{t('overlay.imageNotLoaded')}</span></div>
  return <figure className="my-6 flex justify-center"><img src={asset.dataUrl} alt={block.alt || t('overlay.documentImage')} loading="lazy" decoding="async" draggable={false} className="max-h-[70vh] max-w-full rounded-[4px] object-contain" style={sourceRatio ? { aspectRatio: String(sourceRatio) } : undefined} /></figure>
}

function DocumentCanvas({ document, t }: { document: DocumentPreviewDocument; t: (key: any) => string }) {
  return (
    <div className="min-h-full bg-foreground-3 px-3 py-7 sm:px-8">
      <article className="mx-auto min-h-[calc(100vh-160px)] w-full max-w-[860px] rounded-[7px] border border-foreground/[0.08] bg-background px-7 py-9 shadow-strong sm:px-12 sm:py-12">
        {document.blocks.map((block, index) => block.type === 'table'
          ? <DocumentTable key={index} block={block} />
          : block.type === 'image'
          ? <DocumentImage key={index} block={block} asset={document.assets[block.assetId]} t={t} />
            : <DocumentParagraph key={index} block={block} />)}
        {document.truncated && <div className="mt-8 border-t border-foreground/[0.1] pt-3 text-[11px] text-muted-foreground">{t('overlay.lightPreviewRange')}</div>}
      </article>
    </div>
  )
}

function invalidPreview(props: OfficeDocumentPreviewOverlayProps, t: (key: any) => string) {
  return <PreviewOverlay isOpen={props.isOpen} onClose={props.onClose} theme={props.theme} typeBadge={{ icon: FileText, label: 'Word', variant: 'blue' }} filePath={props.filePath} error={{ label: t('overlay.previewFailed'), message: props.error || t('overlay.previewDataUnavailable') }} embedded={props.embedded}><div className="flex min-h-[180px] items-center justify-center text-sm text-muted-foreground">{t('overlay.openWithSystemApp')}</div></PreviewOverlay>
}

export function OfficeDocumentPreviewOverlay(props: OfficeDocumentPreviewOverlayProps) {
  const { t } = useTranslation()
  const document = React.useMemo(() => parseDocumentPreview(props.content), [props.content])
  if (!document) return invalidPreview(props, t)
  const typeBadge = { icon: FileText, label: 'Word', variant: 'blue' as const }
  const canvas = <DocumentCanvas document={document} t={t} />
  if (props.embedded) {
    if (!props.isOpen) return null
    return <div className="flex h-full w-full flex-col overflow-hidden rounded-[10px] border border-foreground/[0.07] bg-background"><FullscreenOverlayBaseHeader onClose={props.onClose} filePath={props.filePath} typeBadge={typeBadge} subtitle={t('overlay.readOnlyPreview')} />{props.error && <div className="border-b border-destructive/15 bg-destructive/5 px-4 py-2 text-xs text-destructive">{props.error}</div>}<div className="min-h-0 flex-1 overflow-y-auto">{canvas}</div></div>
  }
  return <FullscreenOverlayBase isOpen={props.isOpen} onClose={props.onClose} filePath={props.filePath} typeBadge={typeBadge} subtitle={t('overlay.readOnlyPreview')} contentMode="flow" className="bg-foreground-3">{canvas}</FullscreenOverlayBase>
}
